/**
 * Solarian brand mark.
 *
 * A dimensional rising-sun mark built purely in SVG. Layered:
 *   1. Ambient glow halo       (subtle, animates on mount)
 *   2. Outer orbital ring      (gradient stroke, rotates slowly in :hover)
 *   3. Solid sun disc          (radial gradient, electric blue core)
 *   4. Inner highlight arc     (specular gleam, top-left)
 *
 * Optional wordmark: "SOLARIAN" in geometric caps + tagline underneath.
 *
 * Usage:
 *   <Logo size={36} />               // mark only
 *   <Logo size={36} wordmark />      // mark + "SOLARIAN"
 *   <Logo size={36} wordmark tagline="AI COMMAND CENTER" />
 */

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

const EASE = [0.16, 1, 0.3, 1]

export default function Logo({
  size = 32,
  wordmark = false,
  tagline,
  className,
  animated = true,
}) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <Mark size={size} animated={animated} />
      {wordmark && (
        <div className="flex flex-col min-w-0 leading-none">
          <motion.span
            initial={animated ? { opacity: 0, y: 4 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
            className="font-display tracking-[0.22em] text-[15px] text-white/90"
            style={{ textShadow: '0 0 20px rgba(46, 127, 255, 0.25)' }}
          >
            SOLARIAN
          </motion.span>
          {tagline && (
            <motion.span
              initial={animated ? { opacity: 0, y: 4 } : false}
              animate={{ opacity: 0.5, y: 0 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
              className="font-display tracking-[0.2em] text-[9px] text-white/40 mt-1.5 uppercase"
            >
              {tagline}
            </motion.span>
          )}
        </div>
      )}
    </div>
  )
}

export function Mark({ size = 32, animated = true }) {
  // viewBox is 64×64 — internal coordinates — but we render at `size`.
  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      initial={animated ? { opacity: 0, scale: 0.8, rotate: -20 } : false}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ duration: 0.9, ease: EASE }}
      className="flex-shrink-0 drop-shadow-[0_0_18px_rgba(46,127,255,0.35)]"
    >
      <defs>
        {/* Radial gradient for the sun core */}
        <radialGradient id="sunCore" cx="45%" cy="38%" r="65%">
          <stop offset="0%" stopColor="#8ec3ff" />
          <stop offset="35%" stopColor="#2e7fff" />
          <stop offset="100%" stopColor="#0d3b80" />
        </radialGradient>

        {/* Linear gradient for the orbital ring */}
        <linearGradient id="orbitRing" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6bc0f0" stopOpacity="0.9" />
          <stop offset="50%" stopColor="#2e7fff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#0d3b80" stopOpacity="0.2" />
        </linearGradient>

        {/* Specular highlight gradient — top-left gleam */}
        <radialGradient id="specular" cx="30%" cy="25%" r="35%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.7" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>

        {/* Ambient halo behind the mark */}
        <radialGradient id="halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#2e7fff" stopOpacity="0.5" />
          <stop offset="70%" stopColor="#2e7fff" stopOpacity="0" />
        </radialGradient>

        {/* Glow filter used on the orbit ring */}
        <filter id="orbGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* 1. ambient halo — breathes gently */}
      <motion.circle
        cx="32"
        cy="32"
        r="30"
        fill="url(#halo)"
        initial={animated ? { opacity: 0.3 } : false}
        animate={animated ? { opacity: [0.3, 0.55, 0.3] } : { opacity: 0.45 }}
        transition={
          animated
            ? { duration: 4, repeat: Infinity, ease: 'easeInOut' }
            : undefined
        }
      />

      {/* 2. outer orbital ring — dashed, slowly rotating */}
      <motion.g
        animate={animated ? { rotate: 360 } : { rotate: 0 }}
        transition={
          animated
            ? { duration: 60, repeat: Infinity, ease: 'linear' }
            : undefined
        }
        style={{ originX: '32px', originY: '32px' }}
      >
        <circle
          cx="32"
          cy="32"
          r="27"
          fill="none"
          stroke="url(#orbitRing)"
          strokeWidth="1"
          strokeDasharray="2 6"
          filter="url(#orbGlow)"
          opacity="0.7"
        />
      </motion.g>

      {/* 3. thin solid orbit — static, frames the sun */}
      <circle
        cx="32"
        cy="32"
        r="22"
        fill="none"
        stroke="rgba(107, 192, 240, 0.35)"
        strokeWidth="0.6"
      />

      {/* 4. rising sun wedge */}
      <motion.g
        initial={animated ? { opacity: 0, y: 6 } : false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
      >
        {/* sun disc */}
        <circle cx="32" cy="32" r="16" fill="url(#sunCore)" />
        {/* specular */}
        <circle cx="32" cy="32" r="16" fill="url(#specular)" />

        {/* tiny gleam dot at top-right — the signature detail */}
        <circle
          cx="42"
          cy="22"
          r="1.3"
          fill="#ffffff"
          opacity="0.9"
        />
      </motion.g>

      {/* 5. angular rays — geometric, evenly spaced top half */}
      <motion.g
        initial={animated ? { opacity: 0 } : false}
        animate={{ opacity: 0.9 }}
        transition={{ duration: 0.8, delay: 0.35 }}
      >
        {/* 5 rays across the top half, 30° apart */}
        {[-60, -30, 0, 30, 60].map((deg) => (
          <line
            key={deg}
            x1="32"
            y1="32"
            x2="32"
            y2="8"
            stroke="#6bc0f0"
            strokeWidth="0.6"
            strokeLinecap="round"
            opacity="0.6"
            transform={`rotate(${deg} 32 32)`}
          />
        ))}
      </motion.g>
    </motion.svg>
  )
}
